import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { BodyText, Icon, theme } from '@sg/garnish';

import { type useOrderStatusRequest } from '../../hooks';

export const OrderStatusPayments = (props: OrderStatusPaymentsProps) => {
  const { payments } = props;
  const { formatNumber } = useIntl();

  return (
    <View style={styles.container}>
      <BodyText sizeMatch={['16']} bold>
        <FormattedMessage {...messages.title} />
      </BodyText>

      {payments.map((payment) => (
        <View key={payment.type} style={styles.payment}>
          <View style={styles.paymentType}>
            {payment.icon ? (
              <Icon width={20} height={20} name={payment.icon} />
            ) : null}

            <BodyText sizeMatch={['16']}>
              {payment.metadata ?? (
                <FormattedMessage {...getPaymentType(payment)} />
              )}
            </BodyText>
          </View>

          <BodyText sizeMatch={['16']}>
            {formatNumber(payment.amount, {
              style: 'currency',
              currency: 'USD',
            })}
          </BodyText>
        </View>
      ))}
    </View>
  );
};

const getPaymentType = (
  payment: OrderStatusPaymentsProps['payments'][number],
) => {
  switch (payment.type) {
    case 'GIFT_CARD': {
      return messages.giftCard;
    }

    case 'STIPEND': {
      return messages.stipend;
    }

    default: {
      return messages.creditCard;
    }
  }
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Payment method',
    description: 'Order Status | Payments | Title',
  },
  creditCard: {
    defaultMessage: 'Credit card',
    description: 'Order Status | Payments | Credit card',
  },
  giftCard: {
    defaultMessage: 'Gift card',
    description: 'Order Status | Payments | Gift card',
  },
  stipend: {
    defaultMessage: 'SG Credit',
    description: 'Order Status | Payments | Stipend',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    gap: theme.spacing['4'],
  },
  payment: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  paymentType: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['1'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderStatusPaymentsProps = {
  payments: ReturnType<typeof useOrderStatusRequest>['payments'];
};
